import log from 'loglevel';

// Available log areas
export const LOG_AREAS = ['polling', 'pickFetching', 'labPartner', 'socket', 'modelStore', 'timing'] as const;
export type LogArea = typeof LOG_AREAS[number];

// Log levels from loglevel
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

interface LoggingConfig {
  globalLevel: LogLevel;
  areas: {
    [key in LogArea]: {
      enabled: boolean;
    };
  };
}

const STORAGE_KEY = 'loggingConfig';

// Default configuration
const DEFAULT_CONFIG: LoggingConfig = {
  globalLevel: 'info',
  areas: {
    polling: { enabled: false },
    pickFetching: { enabled: false },
    labPartner: { enabled: false },
    socket: { enabled: false },
    modelStore: { enabled: false },
    timing: { enabled: false }
  }
};

// Initialize configuration from localStorage
const initializeConfig = (): LoggingConfig => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);

      // Merge stored config with defaults to ensure all areas are present
      const mergedConfig: LoggingConfig = {
        globalLevel: parsed.globalLevel || DEFAULT_CONFIG.globalLevel,
        areas: { ...DEFAULT_CONFIG.areas }
      };

      // Override with stored values for existing areas
      if (parsed.areas) {
        for (const area of LOG_AREAS) {
          if (parsed.areas[area]) {
            mergedConfig.areas[area] = parsed.areas[area];
          }
        }
      }

      // Save the merged config back to ensure it's complete
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedConfig));

      return mergedConfig;
    } catch (e) {
      console.error('Failed to parse logging config:', e);
    }
  }
  return DEFAULT_CONFIG;
};

const config: LoggingConfig = initializeConfig();

// Cache of logger instances
const loggers: Map<LogArea, log.Logger> = new Map();

/**
 * Get or create a logger for a specific area
 */
export const getLogger = (area: LogArea): log.Logger => {
  if (!loggers.has(area)) {
    const logger = log.getLogger(area);
    // Apply current configuration
    const areaConfig = config.areas[area];

    // Defensive check: if area config is missing, add default and save
    if (!areaConfig) {
      console.warn(`Area ${area} not found in config, adding with default settings`);
      config.areas[area] = { enabled: false };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      logger.setLevel('silent');
    } else if (areaConfig.enabled) {
      logger.setLevel(config.globalLevel);
    } else {
      logger.setLevel('silent');
    }
    loggers.set(area, logger);
  }
  return loggers.get(area)!;
};

/**
 * Set the global log level
 */
export const setGlobalLogLevel = (level: LogLevel): void => {
  config.globalLevel = level;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

  // Update all enabled loggers
  loggers.forEach((logger, area) => {
    if (config.areas[area].enabled) {
      logger.setLevel(level);
    }
  });
};

/**
 * Enable or disable logging for a specific area
 */
export const setAreaEnabled = (area: LogArea, enabled: boolean): void => {
  config.areas[area].enabled = enabled;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

  // Update the logger
  const logger = getLogger(area);
  if (enabled) {
    logger.setLevel(config.globalLevel);
  } else {
    logger.setLevel('silent');
  }
};

/**
 * Check if an area is enabled
 */
export const isAreaEnabled = (area: LogArea): boolean => {
  return config.areas[area].enabled;
};

/**
 * Get the current logging configuration
 */
export const getLoggingConfig = (): LoggingConfig => {
  return { ...config };
};

/**
 * Get the current global log level
 */
export const getGlobalLogLevel = (): LogLevel => {
  return config.globalLevel;
};
